import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { get, post } from "../../services/api";
import { toast } from "react-toastify";
import DataTable from "react-data-table-component";
import { customTableStyles } from "../../assets/CustomTableStyles";

function ResourceAllocateModal({
  rowData,
  department,
  onClose,
  refreshOnSubmitSuccess,
}) {
  const token = localStorage.getItem("token");

  const {
    register,
    formState: { errors, isSubmitting },
    handleSubmit,
  } = useForm({
    defaultValues: {
      remarks: "",
    },
  });

  const [itDevices, setItDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deviceError, setDeviceError] = useState(null);
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    if (department === "IT") {
      fetchITDevices();
    }
  }, [department, rowData.id]);

  const fetchITDevices = async () => {
    setLoading(true);
    setDeviceError(null);
    try {
      const response = await get(
        `CommonSettings/GetITDeviceByStudentIdAsync?studentId=${rowData.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setItDevices(response);
      const assignedDeviceIds = response
        .filter((device) => device.isAssigned)
        .map((device) => device.id);
      setSelectedDevices(assignedDeviceIds);
    } catch (error) {
      console.error("Error fetching IT devices:", error);
      setDeviceError("Failed to load IT devices.");
      setSelectedDevices([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectedRowsChange = (state) => {
    const selectedIds = state.selectedRows.map((row) => row.id);
    setSelectedDevices(selectedIds);
  };

  const saveData = async (data) => {
    if (department === "IT") {
      const deviceAssignmentsDtos = selectedDevices.map((deviceId) => ({
        id: 0,
        itDeviceId: deviceId,
        studentId: rowData.id,
        dateAssigned: new Date().toISOString(),
        dateReturned: null,
        status: data.remarks,
        isActive: true,
      }));

      const formData = {
        deviceAssignmentsDtos,
      };

      try {
        const response = await post(
          "CommonSettings/AssignITDeviceAsync",
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (response.isSuccess) {
          toast.success("Devices assigned successfully!");
          onClose();
          refreshOnSubmitSuccess();
        } else {
          toast.error(response.message || "An unexpected error occurred");
        }
      } catch (error) {
        console.error("Error assigning devices:", error);
        toast.error("Failed to assign devices.");
      }
    } else if (department === "Uniform" || department === "Stationery") {
      const formData = {
        id: rowData.id,
        isStatus: true,
        approvalDate: new Date().toISOString(),
        remarks: data.remarks,
        departmentName: department,
      };

      try {
        const response = await post(
          "Student/UpdateStudentStatusDepartmentwiseById",
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (response.isSuccess) {
          toast.success("Status updated successfully!");
          onClose();
          refreshOnSubmitSuccess();
        } else {
          toast.error(response.message || "An unexpected error occurred");
        }
      } catch (error) {
        console.error("Error updating status:", error);
        toast.error("Failed to update status.");
      }
    }
  };

  const columns = [
    {
      name: "Sr No.",
      cell: (row, index) => (currentPage - 1) * rowsPerPage + index + 1,
      sortable: false,
      width: "60px",
    },
    {
      name: "Name",
      selector: (row) => row.name,
      sortable: true,
    },
    {
      name: "Device Type",
      selector: (row) => row.deviceType,
      sortable: true,
    },
    {
      name: "Brand",
      selector: (row) => row.brand,
      sortable: true,
    },
    {
      name: "Status",
      selector: (row) =>
        row.isAssigned ? (
          <span className="badge bg-success fs-10">Assigned</span>
        ) : (
          <span className="badge bg-secondary fs-10">Not Assigned</span>
        ),
      sortable: true,
      width: "120px",
    },
  ];

  return (
    <>
      <div
        className="modal fade show"
        tabIndex="-1"
        style={{ display: "block" }}
      >
        <div className="modal-dialog modal-md">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="mb-0">Resource Allocation - {department}</h5>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
              ></button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleSubmit(saveData)}>
                <div className="mb-3">
                  <label className="form-label">
                    Allocation Remarks{" "}
                    <span className="text-danger fs-9">*</span>
                  </label>
                  <textarea
                    {...register("remarks", {
                      required: "This field is required.",
                    })}
                    className="form-control"
                  ></textarea>
                  {errors.remarks && (
                    <small className="text-danger">
                      {errors.remarks.message}
                    </small>
                  )}
                </div>
                {department === "IT" && (
                  <div className="mb-3">
                    <h6>IT Devices</h6>
                    {loading ? (
                      <p>Loading IT devices...</p>
                    ) : deviceError ? (
                      <p className="text-danger">{deviceError}</p>
                    ) : (
                      <DataTable
                        columns={columns}
                        data={itDevices}
                        pagination
                        paginationPerPage={rowsPerPage}
                        paginationRowsPerPageOptions={[10, 20, 30, 40, 50]}
                        dense
                        customStyles={customTableStyles}
                        highlightOnHover={true}
                        selectableRows
                        onSelectedRowsChange={handleSelectedRowsChange}
                        selectableRowsSelected={(row) =>
                          selectedDevices.includes(row.id)
                        }
                        selectableRowDisabled={(row) => row.isAssigned}
                        onChangePage={setCurrentPage}
                        onChangeRowsPerPage={setRowsPerPage}
                        keyField="id"
                      />
                    )}
                  </div>
                )}
                <div className="d-flex gap-2 justify-content-end">
                  <button
                    type="submit"
                    className="btn btn-primary btn-sm"
                    disabled={
                      isSubmitting ||
                      (department === "IT" && selectedDevices.length === 0)
                    }
                  >
                    {isSubmitting ? "Submitting..." : "Submit"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show"></div>
    </>
  );
}

export default ResourceAllocateModal;
